import {
  ApplicationRef,
  ComponentRef,
  EmbeddedViewRef,
  Injectable,
  Injector,
  createComponent,
  inject,
} from '@angular/core';
import { Subject } from 'rxjs';

import { DataLayerService } from '@modules/shared/services/data-layer.service';
import { ANALYTIC_EVENTS, TrackingService, TRACKING_IDENTIFIER } from '@modules/shared/services/tracking.service';

import { FullscreenService } from '../fullscreen.service';
import { MediaFlyoutComponent } from '../media-flyout/media-flyout.component';
import { MediaVideoModel } from '../models/media-video.model';
import { PlayerModel, PlayerModelOptionsInterface } from '../models/player.model';

@Injectable()
export class PlayerService {
  private fullscreenService = inject(FullscreenService);
  private trackingService = inject(TrackingService);
  private appRef = inject(ApplicationRef);
  private injector = inject(Injector);
  private dataLayerService = inject(DataLayerService);

  private componentRef!: ComponentRef<MediaFlyoutComponent>;
  private _playerClosed$ = new Subject<boolean>();

  public activePlayer!: PlayerModel;

  public createPlayer(playerData: MediaVideoModel, options: PlayerModelOptionsInterface = {}): PlayerModel {
    this.trackingService.push(location.href, {
      events: ANALYTIC_EVENTS.VIDEO_PLAYED,
      [TRACKING_IDENTIFIER.VIDEO_STATUS]: playerData.status,
      [TRACKING_IDENTIFIER.RESOURCE_ID]: playerData.id,
    });
    return new PlayerModel(playerData, this.fullscreenService, this.dataLayerService, options);
  }

  public createFlyout(player: PlayerModel) {
    this.activePlayer = player;
    this.componentRef = createComponent(MediaFlyoutComponent, {
      environmentInjector: this.appRef.injector,
      elementInjector: this.injector,
    });
    this.componentRef.instance.player = player;
    this.appRef.attachView(this.componentRef.hostView);

    const domElem = (this.componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;

    player.flyoutRef = this.componentRef;
    document.body.appendChild(domElem);
  }

  public destroyFlyout(continuePlaying = false) {
    if (!continuePlaying && this.activePlayer) {
      this.activePlayer.pause();
      this._playerClosed$.next(true);
    }
    if (this.componentRef) {
      this.appRef.detachView(this.componentRef.hostView);
      this.componentRef.destroy();
    }
  }

  get playerClosed$() {
    return this._playerClosed$.asObservable();
  }
}
